import { useId } from "react";
import type { SystemStatus } from "@/domain/systemAnalysis";
import type { TrendValue } from "@/domain/systemAnalysisTrend";

/** The `.system-status-*` colours in app.css, as in the single analysis. */
const STATUS_COLORS: Record<SystemStatus, string> = {
  fulfilled: "#3a9b48",
  almost_fulfilled: "#b4c935",
  partially_fulfilled: "#f3f02f",
  only_limited: "#f08a24",
  not_fulfilled: "#db4b49",
  insufficient_information: "#4f8fb8",
  zero: "#666666",
  unknown: "#ffffff",
};
const DARK_LABEL = new Set<SystemStatus>([
  "almost_fulfilled",
  "partially_fulfilled",
  "only_limited",
]);
const NOT_ASSESSED_STROKE = "#9aa6ab";
const NOT_ASSESSED_LINE = "#b9c3c7";

function labelColor(status: SystemStatus): string {
  return status === "unknown" ? "#7b878c" : DARK_LABEL.has(status) ? "#1d2a30" : "#ffffff";
}

const WIDTH = 270;
const HEIGHT = 44;
const HEADER_HEIGHT = 26;
const FIRST_YEAR_X = [60, 230];

const FIRST_X = 38;
const LAST_X = 230;
/** Below this column spacing, year labels are shortened to "’24". */
const FULL_LABEL_SPACING = 36;

/** Column centres for the years of the period, evenly spaced. */
function columnsFor(count: number): number[] {
  if (count < 3) return FIRST_YEAR_X;
  const step = (LAST_X - FIRST_X) / (count - 1);
  return Array.from({ length: count }, (_, index) => FIRST_X + index * step);
}

/** F at the top, N at the bottom; averages ("all locations") fall in between. */
function yFor(value: TrendValue): number {
  return value.points === null ? 22 : 33 - value.points * 11;
}

function Dot({ x, y, r, status }: { x: number; y: number; r: number; status: SystemStatus }) {
  return (
    <circle
      cx={x}
      cy={y}
      r={r}
      fill={STATUS_COLORS[status]}
      stroke={status === "unknown" ? NOT_ASSESSED_STROKE : "#ffffff"}
      strokeWidth={1.5}
    />
  );
}

function LatestDot({
  x,
  y,
  status,
  id,
}: {
  x: number;
  y: number;
  status: SystemStatus;
  id: string;
}) {
  return (
    <>
      <Dot x={x} y={y} r={10} status={status} />
      <text x={x} y={y + 3.3} textAnchor="middle" fontSize={9.5} fill={labelColor(status)}>
        {id.toUpperCase().replace(/\./g, "")}
      </text>
    </>
  );
}

interface CheckpointTrendRowProps {
  id: string;
  /** One value per year of the period, oldest first (one in "firstYear" mode). */
  values: TrendValue[];
  mode: "history" | "firstYear";
  ariaLabel: string;
}

export function CheckpointTrendRow({ id, values, mode, ariaLabel }: CheckpointTrendRowProps) {
  // Keeps url(#…) references plain whatever characters useId produces.
  const gradientId = `trend${useId().replace(/[^A-Za-z0-9_-]/g, "")}`;
  const xs = mode === "firstYear" ? FIRST_YEAR_X : columnsFor(values.length);
  const points = values.map((value, index) => ({ value, x: xs[index]!, y: yFor(value) }));
  const latest = points.at(-1);

  return (
    <svg
      className="system-trend-svg"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width={WIDTH}
      height={HEIGHT}
      role="img"
      aria-label={ariaLabel}
    >
      {mode === "firstYear" && latest ? (
        <>
          <line x1={xs[0]} y1={3} x2={xs[0]} y2={41} stroke="#e3e8eb" />
          <line x1={xs[1]} y1={3} x2={xs[1]} y2={41} stroke="#e3e8eb" strokeDasharray="2 3" />
          <line
            x1={72}
            y1={latest.y}
            x2={218}
            y2={latest.y}
            stroke={NOT_ASSESSED_LINE}
            strokeWidth={2.5}
            strokeDasharray="4 5"
            strokeLinecap="round"
          />
          <LatestDot x={latest.x} y={latest.y} status={latest.value.source} id={id} />
          <circle
            cx={xs[1]}
            cy={latest.y}
            r={10}
            fill="#ffffff"
            stroke={NOT_ASSESSED_STROKE}
            strokeWidth={1.5}
            strokeDasharray="3 3"
          />
        </>
      ) : (
        <>
          {points.map((point, index) => (
            <line
              key={`column-${index}`}
              x1={point.x}
              y1={3}
              x2={point.x}
              y2={41}
              stroke={index === points.length - 1 ? "#e3e8eb" : "#eef1f3"}
            />
          ))}
          <defs>
            {points.slice(1).map((end, index) => {
              const start = points[index]!;
              const color = (value: TrendValue) =>
                value.source === "unknown" ? NOT_ASSESSED_LINE : STATUS_COLORS[value.source];
              return (
                <linearGradient
                  key={index}
                  id={`${gradientId}-${index}`}
                  gradientUnits="userSpaceOnUse"
                  x1={start.x}
                  y1={start.y}
                  x2={end.x}
                  y2={end.y}
                >
                  <stop offset={0.12} stopColor={color(start.value)} />
                  <stop offset={0.88} stopColor={color(end.value)} />
                </linearGradient>
              );
            })}
          </defs>
          {points.slice(1).map((end, index) => {
            const start = points[index]!;
            const dashed = start.value.points === null || end.value.points === null;
            return (
              <line
                key={`segment-${index}`}
                x1={start.x}
                y1={start.y}
                x2={end.x}
                y2={end.y}
                stroke={`url(#${gradientId}-${index})`}
                strokeWidth={3}
                strokeLinecap="round"
                strokeDasharray={dashed ? "3 3" : undefined}
              />
            );
          })}
          {points.slice(0, -1).map((point, index) => (
            <Dot key={`dot-${index}`} x={point.x} y={point.y} r={6} status={point.value.source} />
          ))}
          {latest && <LatestDot x={latest.x} y={latest.y} status={latest.value.source} id={id} />}
        </>
      )}
    </svg>
  );
}

/** Year labels above the rows, on the same x positions. */
export function TrendYearsHeader({
  years,
  mode,
}: {
  years: number[];
  mode: "history" | "firstYear";
}) {
  const xs = mode === "firstYear" ? FIRST_YEAR_X : columnsFor(years.length);
  const labels = mode === "firstYear" ? [years[0]!, years[0]! + 1] : years;
  const short = labels.length > 2 && (LAST_X - FIRST_X) / (labels.length - 1) < FULL_LABEL_SPACING;
  return (
    <svg
      className="system-trend-svg"
      viewBox={`0 0 ${WIDTH} ${HEADER_HEIGHT}`}
      width={WIDTH}
      height={HEADER_HEIGHT}
      aria-hidden="true"
    >
      {labels.map((year, index) => {
        const emphasised = mode === "firstYear" ? index === 0 : index === labels.length - 1;
        const upcoming = mode === "firstYear" && index === 1;
        return (
          <text
            key={year}
            x={xs[index]}
            y={17}
            textAnchor="middle"
            fontSize={12}
            fontWeight={emphasised ? 700 : 400}
            fontStyle={upcoming ? "italic" : undefined}
            fill={emphasised ? "#0b3c4b" : upcoming ? "#7b878c" : "#5b6870"}
          >
            {short ? `’${String(year).slice(-2)}` : year}
          </text>
        );
      })}
    </svg>
  );
}
